'use client';

import * as Headless from '@headlessui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { LayoutGroup, motion } from 'framer-motion';
import React, { forwardRef, useId } from 'react';
import { TouchTarget } from './button';
import { Link } from './link';
import { cn } from '../../lib/utils';

const navbarVariants = cva(['flex flex-1 items-center gap-4 py-2.5']);

export interface NavbarProps
  extends React.ComponentPropsWithoutRef<'nav'>,
    VariantProps<typeof navbarVariants> {
  className?: string;
  variant?: 'default' | 'crimson' | 'mint' | 'blue' | 'black';
}

export function Navbar({
  className,
  variant = 'default',
  ...props
}: NavbarProps) {
  return (
    <nav {...props} className={cn(navbarVariants(), className)} />
  );
}

const navbarDividerVariants = cva([
  'h-6 w-px bg-border dark:bg-border',
]);

export interface NavbarDividerProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof navbarDividerVariants> {
  className?: string;
}

export function NavbarDivider({
  className,
  ...props
}: NavbarDividerProps) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={cn(navbarDividerVariants(), className)}
    />
  );
}

const navbarSectionVariants = cva(['flex items-center gap-3']);

export interface NavbarSectionProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof navbarSectionVariants> {
  className?: string;
}

export function NavbarSection({
  className,
  ...props
}: NavbarSectionProps) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div
        {...props}
        className={cn(navbarSectionVariants(), className)}
      />
    </LayoutGroup>
  );
}

const navbarSpacerVariants = cva(['-ml-4 flex-1']);

export interface NavbarSpacerProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof navbarSpacerVariants> {
  className?: string;
}

export function NavbarSpacer({
  className,
  ...props
}: NavbarSpacerProps) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={cn(navbarSpacerVariants(), className)}
    />
  );
}

const navbarItemVariants = cva(
  [
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium text-foreground sm:text-sm/5',
    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-muted-foreground sm:*:data-[slot=icon]:size-5',
    // Trailing icon (down chevron or similar)
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5 sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    // Avatar
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 sm:*:data-[slot=avatar]:size-6',
    // Hover
    'hover:bg-muted hover:*:data-[slot=icon]:fill-foreground',
    // Active
    'data-[state=active]:bg-muted data-[state=active]:*:data-[slot=icon]:fill-foreground',
  ],
  {
    variants: {
      variant: {
        default: 'data-[current=true]:after:bg-primary',
        crimson:
          'data-[current=true]:after:bg-flame hover:text-flame',
        mint: 'data-[current=true]:after:bg-sunlight hover:text-sunlight',
        blue: 'data-[current=true]:after:bg-ocean hover:text-ocean',
        black:
          'data-[current=true]:after:bg-black dark:data-[current=true]:after:bg-white hover:text-black dark:hover:text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface NavbarItemProps
  extends VariantProps<typeof navbarItemVariants> {
  current?: boolean;
  className?: string;
  children: React.ReactNode;
  variant?: 'default' | 'crimson' | 'mint' | 'blue' | 'black';
}

export const NavbarItem = forwardRef(function NavbarItem(
  {
    current,
    className,
    children,
    variant = 'default',
    ...props
  }: NavbarItemProps &
    (
      | Omit<Headless.ButtonProps, 'as' | 'className'>
      | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
    ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = cn(navbarItemVariants({ variant }), className);

  return (
    <span className={cn('relative', className)}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-x-2 -bottom-2.5 h-0.5 rounded-full bg-foreground dark:bg-foreground"
        />
      )}
      {'href' in props ? (
        <Link
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...props}
          className={cn('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

const navbarLabelVariants = cva(['truncate']);

export interface NavbarLabelProps
  extends React.ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof navbarLabelVariants> {
  className?: string;
}

export function NavbarLabel({
  className,
  ...props
}: NavbarLabelProps) {
  return (
    <span
      {...props}
      className={cn(navbarLabelVariants(), className)}
    />
  );
}

export {
  navbarVariants,
  navbarDividerVariants,
  navbarSectionVariants,
  navbarSpacerVariants,
  navbarItemVariants,
  navbarLabelVariants,
};
